import React, { useState } from "react";
import PropTypes from "prop-types";

// Thumbs up for one lab - on its /labs card and in its own page's topbar. Up
// votes only: clicking again takes the vote back. Mounted from
// app/views/labs/_vote.html.haml with props from LabsHelper#lab_vote_props.
//
// Signed in only, so the count means one person each. Anonymous visitors see the
// count, and clicking it offers log in / sign up (both come back to this page)
// rather than sending them off the moment they touch it.
export default function VoteButton({ count: initialCount, logInUrl, signUpUrl, signedIn, variant = "light", voted: initialVoted, voteUrl }) {
  const [count, setCount] = useState(initialCount);
  const [voted, setVoted] = useState(initialVoted);
  const [sending, setSending] = useState(false);
  const [askToLogIn, setAskToLogIn] = useState(false);

  const toggle = () => {
    if (!signedIn) {
      setAskToLogIn(true);
      return;
    }
    if (sending) return;

    const nextVoted = !voted;
    // Optimistic, then corrected by the server's count - which also picks up
    // votes cast since the page loaded.
    setVoted(nextVoted);
    setCount((current) => Math.max(0, current + (nextVoted ? 1 : -1)));
    setSending(true);

    const token = document.querySelector("meta[name='csrf-token']");

    fetch(voteUrl, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
      },
      method: nextVoted ? "POST" : "DELETE",
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => {
        setVoted(data.voted);
        setCount(data.count);
      })
      .catch(() => {
        setVoted(!nextVoted);
        setCount((current) => Math.max(0, current + (nextVoted ? -1 : 1)));
      })
      .finally(() => setSending(false));
  };

  const label = voted ? "Remove your vote for this lab" : "Vote for this lab";

  return (
    <span className={`labs-vote labs-vote-${variant}`}>
      <button
        aria-label={`${label} (${count} ${count === 1 ? "vote" : "votes"})`}
        aria-pressed={signedIn ? voted : undefined}
        className={`labs-vote-button${voted ? " labs-vote-voted" : ""}`}
        onClick={toggle}
        title={signedIn ? label : "Log in to vote"}
        type="button"
      >
        <svg aria-hidden="true" className="labs-vote-icon" viewBox="0 0 24 24">
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3m7-2V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z" />
        </svg>
        <span className="labs-vote-count">{count}</span>
      </button>
      {askToLogIn && (
        <span className="labs-vote-login">
          <a href={logInUrl}>Log in</a> or <a href={signUpUrl}>sign up</a> to vote
        </span>
      )}
    </span>
  );
}

VoteButton.propTypes = {
  count: PropTypes.number.isRequired,
  logInUrl: PropTypes.string.isRequired,
  signUpUrl: PropTypes.string.isRequired,
  signedIn: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(["light", "dark"]),
  voteUrl: PropTypes.string.isRequired,
  voted: PropTypes.bool.isRequired,
};
